import { Calendar, Mail, Phone, Pin, Wallet } from "lucide-react";
import React, { } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatCurrencyAmount, getFormattedDate, getPhoneNumberFromShortCodeFormat, sortArrayByAlphabets } from "../../../../utils/miscFunctions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Loader from "../../../../components/Loader";
import BackButton from "../../../../components/BackButton";
import ActionsPopOnMe from "../../../../components/ActionsPopOnMe";
import useRoute from "../../../../hooks/routesHook";
import clsx from "clsx";
import useModal from "../../../../hooks/modalHook";
import useBills from "../../../../hooks/billsHook";

const BillDetailPage: React.FC = () => {
  const { billId } = useParams<{ billId?: string }>();
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const { ModalComponent, updateModalStates, closeModal } = useModal()
  const { StatusBadge, handleMarkAsPaid, fetchBillDetail, BillsModalComponent } = useBills({});
  const queryClient = useQueryClient();



  const { data: billDetail, isLoading } = useQuery({
    queryKey: ['fetchBillDetail', billId],
    queryFn: () => fetchBillDetail(),
    enabled: !!billId,
  });



  const getBillActions = () => {
    const actions = [];

    if (!billDetail?.paid) {
      actions.push(
        {
          label: 'Pay offline',
          icon: <Wallet className='h-4 w-4' />,
          callback: () => { if (billDetail) handleMarkAsPaid(billDetail) }
        }
      );
    }

    return sortArrayByAlphabets(actions);
  };

  return (
    <>
      {!billDetail ? (
        <div className="flex justify-center items-center h-screen">
          <Loader message="Loading. Please wait..." brollyLoaderFixed />
        </div>
      ) : (
        <div className="mx-auto">

          {/* Back Button */}
          <div className="mb-6 flex justify-between items-center">
            <BackButton onClick={() => navigate(`${getDashboardBase()}/bills`)} />

            {getBillActions()?.length > 0 && (
              <ActionsPopOnMe actions={getBillActions()} />
            )}
          </div>

          {/* Bill Header */}
          <div className="bg-white p-6 rounded-lg shadow mb-6 dark:bg-darkBg dark:border dark:border-darkBg-border">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center">
              <div>
                <div className="flex items-center mb-2">
                  <h1 className="text-2xl font-bold mr-3 dark:text-darkBg-mainText">Bill #{billDetail.id}</h1>
                  <StatusBadge status={billDetail.paid} />
                </div>
                <p className="text-gray-500 mb-2 dark:text-darkBg-subText">
                  From Brolly
                </p>
                <p className="text-sm text-gray-500 dark:text-darkBg-subText">
                  Invoice #: {billDetail.referenceNumber}
                </p>
              </div>

              <div className="mt-4 md:mt-0 text-right">
                <div className="text-gray-500 mb-1 dark:text-darkBg-subText">Amount Due</div>
                <p className="text-3xl font-bold dark:text-darkBg-mainText">
                  {formatCurrencyAmount(billDetail.amount)}
                </p>
                <div className="flex items-center mt-2 justify-end">
                  <Calendar className="w-4 h-4 mr-2 text-gray-500 dark:text-darkBg-subText" />
                  <span className="dark:text-darkBg-mainText">
                    Due: {getFormattedDate({ date: new Date(billDetail.dueDate) })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Bill Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            {/* Left Column - Basic Details */}
            <div className="col-span-2">
              <BillDetailsItemCard title="Bill Details" classes="col-span-1">
                <div className="">
                  {/* Vendor information */}
                  <div className="mb-6">
                    <h3 className="text-sm uppercase text-gray-500 font-medium mb-2 dark:text-darkBg-subText">Company Information</h3>
                    <div className="p-4 border border-gray-200 bg-gray-50 rounded-md dark:border-darkBg-border dark:bg-darkBg">
                      <p className="text-lg font-medium dark:text-darkBg-mainText">{billDetail?.broker?.name || "-"}</p>
                      {/* <p className="text-sm text-gray-600 dark:text-darkBg-subText">{billDetail?.broker?.address || "-"}</p> */}
                      <div className="mt-2 text-sm">
                        <div className="flex items-center"><Pin className="h-4 w-4 mr-2 text-gray-300 dark:text-gray-400" /><span className="dark:text-darkBg-mainText">{billDetail?.broker?.address || "-"}</span></div>
                        <div className="flex items-center"><Mail className="h-4 w-4 mr-2 text-gray-300 dark:text-gray-400" /><span className="dark:text-darkBg-mainText">{billDetail?.broker?.email || "-"}</span></div>
                        <div className="flex items-center"><Phone className="h-4 w-4 mr-2 text-gray-300 dark:text-gray-400" /><span className="dark:text-darkBg-mainText">{billDetail?.broker?.mobilePhone ? getPhoneNumberFromShortCodeFormat(billDetail?.broker?.mobilePhone) : "-"}</span></div>
                      </div>
                    </div>
                  </div>


                  {/* Notes */}
                  {billDetail.description && (
                    <div className="mb-6">
                      <h3 className="text-sm uppercase text-gray-500 font-medium mb-2 dark:text-darkBg-subText">Description</h3>
                      <div className="p-4 border border-gray-200 rounded-md bg-gray-50 whitespace-pre-line dark:border-darkBg-border dark:bg-darkBg dark:text-darkBg-mainText">
                        {billDetail.description || 'No description available'}
                      </div>
                    </div>
                  )}
                </div>
              </BillDetailsItemCard>
            </div>

            {/* Right Column - Payment Details & Documents */}
            <div className="col-span-1">

              {/* Payment Details */}
              <BillDetailsItemCard title="Payment Details" classes="col-span-1">
                <div className="">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-darkBg-subText">Status:</span>
                      <StatusBadge status={billDetail.paid} />
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-darkBg-subText">Issue Date:</span>
                      <span className="font-medium dark:text-darkBg-mainText">{getFormattedDate({ date: billDetail.createdOn })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-darkBg-subText">Due Date:</span>
                      <span className={`font-medium ${billDetail.status === 'overdue' ? 'text-red-600 dark:text-red-400' : 'dark:text-darkBg-mainText'}`}>
                        {getFormattedDate({ date: billDetail.dueDate })}
                      </span>
                    </div>

                    {billDetail.status === 'paid' && (
                      <>
                        <div className="pt-3 border-t border-gray-200 mt-3 dark:border-darkBg-border">
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-darkBg-subText">Payment Date:</span>
                            <span className="font-medium dark:text-darkBg-mainText">
                              {/* {billDetail.paidOn && getFormattedDate({date: billDetail.paidOn})} */}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-darkBg-subText">Payment Method:</span>
                            <span className="font-medium dark:text-darkBg-mainText">{billDetail.broker?.paymentAccount.accountType}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-darkBg-subText">Reference:</span>
                            <span className="font-medium dark:text-darkBg-mainText">{billDetail.broker?.paymentAccount.accountNumber}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </BillDetailsItemCard>

              {/* Documents */}
              {/* <DocumentDownloadCard title="Documents" documents={[]} /> */}
            </div>
          </div>
        </div>
      )}
      <ModalComponent />
      <BillsModalComponent/>
    </>
  );
};

export default BillDetailPage;

type BillDetailsItemProps = {
  name?: string;
  value?: string | number | JSX.Element;
  children?: React.ReactNode;
  classes?: string;
};

interface IBillDetailsItemCard {
  title: string;
  classes?: string;
  children: JSX.Element;
  icon?: React.ReactElement;
}

const BillDetailsItem = ({ name, value, children, classes }: BillDetailsItemProps) => {
  return (
    <div className={clsx(`flex flex-col py-1 `, classes)}>
      <div className="text-gray-600 dark:text-darkBg-subText">{name && `${name}:`}</div>
      <div className="flex items-center gap-1">
        <span className="truncate text-[14px] font-medium whitespace-nowrap dark:text-darkBg-mainText">{value}</span>
      </div>
      {children}
    </div>
  );
};

const BillDetailsItemCard = ({
  title,
  classes,
  children,
  icon
}: IBillDetailsItemCard): JSX.Element => {
  return (
    <div className="mt-5 bg-white rounded-2xl shadow-md overflow-hidden dark:bg-darkBg dark:border dark:border-darkBg-border">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 dark:bg-darkBg  dark:border-darkBg-border">
        <h2 className="text-lg font-medium text-gray-900 flex items-center dark:text-darkBg-mainText">
          {icon}
          {title}
        </h2>
      </div>
      <div
        className={clsx(
          'grid py-6  px-4 rounded-lg h-full grid-cols-1 gap-5 ',
          classes ? classes : 'md:grid-cols-3 '
        )}>
        {children}
      </div>
    </div>
  );
};